import React, { useState } from 'react';
import type { ArsClaimPayment, PaymentReconciliation } from '../../../services/arsClaimService';
import arsClaimService from '../../../services/arsClaimService';
import { formatDate } from '../../../lib/format';

interface Props {
    claimId: number;
    payments: ArsClaimPayment[];
    reconciliation: PaymentReconciliation | null;
    onUpdate: () => void;
}

const ClaimPaymentsSection: React.FC<Props> = ({ claimId, payments, reconciliation, onUpdate }) => {
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({ amount: '', reference: '', date: '', notes: '' });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.amount || !formData.reference) {
            alert('Monto y referencia son requeridos');
            return;
        }
        setSubmitting(true);
        try {
            await arsClaimService.recordPayment(claimId, {
                amount: parseFloat(formData.amount),
                payment_reference: formData.reference,
                payment_date: formData.date || undefined,
                notes: formData.notes || undefined,
            });
            setFormData({ amount: '', reference: '', date: '', notes: '' });
            setShowForm(false);
            onUpdate();
        } catch {
            alert('Error al registrar pago');
        } finally {
            setSubmitting(false);
        }
    };

    const statusLabel = reconciliation?.status === 'paid' ? 'Pagado' : reconciliation?.status === 'partial' ? 'Parcial' : 'Pendiente';
    const statusClass = reconciliation?.status === 'paid' ? 'text-success' : reconciliation?.status === 'partial' ? 'text-warning' : 'text-danger';

    return (
        <div className="card mb-3">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Pagos Recibidos</h5>
                    <button className="btn btn-sm btn-primary" onClick={() => setShowForm(!showForm)}>
                        {showForm ? 'Cancelar' : 'Registrar Pago'}
                    </button>
                </div>

                {reconciliation && (
                    <div className="row mb-3 g-2">
                        <div className="col-md-3">
                            <div className="border rounded p-2 text-center">
                                <small className="text-muted d-block">Reclamado</small>
                                <strong>${reconciliation.claimed_amount.toLocaleString('es-DO', { minimumFractionDigits: 2 })}</strong>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="border rounded p-2 text-center">
                                <small className="text-muted d-block">Pagado</small>
                                <strong>${reconciliation.total_paid.toLocaleString('es-DO', { minimumFractionDigits: 2 })}</strong>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="border rounded p-2 text-center">
                                <small className="text-muted d-block">Balance</small>
                                <strong className={statusClass}>
                                    ${reconciliation.remaining_balance.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                                </strong>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="border rounded p-2 text-center">
                                <small className="text-muted d-block">Estado</small>
                                <strong className={statusClass}>{statusLabel}</strong>
                            </div>
                        </div>
                    </div>
                )}

                {reconciliation?.discrepancy_warning && (
                    <div className="alert alert-warning py-2 small">
                        <i className="fa fa-exclamation-triangle me-1" />
                        {reconciliation.discrepancy_warning}
                    </div>
                )}

                {showForm && (
                    <form onSubmit={handleSubmit} className="mb-3 p-3 bg-light rounded">
                        <div className="row g-2">
                            <div className="col-md-3">
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    placeholder="Monto *"
                                    step="0.01"
                                    min="0.01"
                                    required
                                    value={formData.amount}
                                    onChange={e => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                                />
                            </div>
                            <div className="col-md-3">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Referencia *"
                                    required
                                    value={formData.reference}
                                    onChange={e => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                                />
                            </div>
                            <div className="col-md-2">
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={formData.date}
                                    onChange={e => setFormData(prev => ({ ...prev, date: e.target.value }))}
                                />
                            </div>
                            <div className="col-md-2">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Notas"
                                    value={formData.notes}
                                    onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                />
                            </div>
                            <div className="col-md-2">
                                <button type="submit" className="btn btn-sm btn-primary w-100" disabled={submitting}>
                                    {submitting ? 'Guardando...' : 'Guardar'}
                                </button>
                            </div>
                        </div>
                    </form>
                )}

                {payments.length === 0 && !showForm && (
                    <p className="text-muted">No hay pagos registrados.</p>
                )}

                {payments.length > 0 && (
                    <div className="table-responsive">
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Referencia</th>
                                    <th>Monto</th>
                                    <th>Notas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map(payment => (
                                    <tr key={payment.id}>
                                        <td>{formatDate(payment.payment_date)}</td>
                                        <td><code>{payment.payment_reference}</code></td>
                                        <td>${payment.amount.toLocaleString('es-DO', { minimumFractionDigits: 2 })}</td>
                                        <td>{payment.notes || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClaimPaymentsSection;
